import React, { useState, useMemo } from "react";
import CustomTable from "components/Table/CustomTable";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { useModal } from "contexts/ModalProvider";
import { RejectedPartnersSchema } from "model/partners/rejected.schema";
import Button from "reactstrap/lib/Button";
import check from "assets/icons/check-square.svg";
import slash from "assets/icons/slash-circle.svg";
import useTableChangeHandler from "hooks/useTableChangeHandler";
import { useToasts } from "utils/useHotToast";
import ToastNotification from "components/Toast/ToastNotification";
import Avatar from "components/Avatar/Avatar";
import { useUpdatePartnerStatusMutation } from "services/partners/api";

const RejectedPartnersTable = ({ data, error, isLoading, onTableChange }) => {
  const { handleSort, handleFilter, handleSearch, handlePageChange, forceRefresh } = useTableChangeHandler(onTableChange);

  const { modal } = useModal();
  const [selectedRejectedPartners, setSelectedRejectedPartners] = useState([]);
  const { addToast, removeToast } = useToasts();
  const [updatePartnerStatus] = useUpdatePartnerStatusMutation();

  const columns = useMemo(
    () => [
      {
        header: "Affiliate พาร์ทเนอร์",
        accessor: "name",
        sortable: true,
        render: (item) => (
          <div className="partner-info d-flex align-items-center gap-3">
            <Avatar src={item.profilePicture} alt={`${item.firstName} ${item.lastName}`} />
            <div>
              <div className="text-lg fw-semibold">{`${item.firstName} ${item.lastName}`}</div>
              <div className="text-md text-gray-500">{item.email}</div>
            </div>
          </div>
        ),
      },
      {
        header: "ไอดีสมาชิก",
        accessor: "id",
        responsive: "lg",
      },
      {
        header: "วันที่สมัครสมาชิก",
        accessor: "registeredAt",
        align: "center",
        responsive: "md",
        render: (item) => dayjs(item.registeredAt).format("YYYY-MM-DD"),
      },
      {
        header: "เบอร์โทรศัพท์",
        accessor: "phoneNo",
        responsive: "md",
      },
      {
        header: "ข้อมูลโปรไฟล์",
        accessor: "info",
        align: "center",
        render: (item) => (
          <Link to={`/partners/${item.id}?source=hidden`} className="light">
            <i className="mdi mdi-eye-outline" />
            ดูข้อมูล
          </Link>
        ),
      },
      {
        header: "",
        accessor: "actions",
        width: 50,
        render: (item) => (
          <div className="d-flex justify-content-end gap-2">
            <Button
              color="primary"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                handleApprovePartner(item);
              }}
            >
              <i className="mdi mdi-check" />
              อนุมัติ
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  const renderUser = (item) => (
    <div className="partner-info d-flex align-items-center gap-3">
      <Avatar src={item.profilePicture} alt={`${item.firstName} ${item.lastName}`} />
      <div>
        <div className="text-lg fw-semibold">{`${item.firstName} ${item.lastName}`}</div>
        <div className="text-md text-gray-500">{item.email}</div>
      </div>
    </div>
  );

  const handleApprovePartner = async (item) => {
    const result = await modal.alert.primary({
      title: "คุณยืนยันพาร์ทเนอร์ท่านนี้หรือไม่?",
      message: `ตรวจสอบรายชื่อพาร์ทเนอร์ที่คุณต้องการว่าถูกต้องหรือไม่? เมื่ออนุมัติแล้ว รายชื่อพาร์ทเนอร์ที่อนุมัติจะไปอยู่ในแถบของ "พาร์ทเนอร์ในระบบ"?`,
      children: renderUser(item),
      confirmText: "ยืนยันที่จะอนุมัติ",
      cancelText: "ยกเลิก",
    });

    if (result) {
      try {
        // Update status to Approved
        await updatePartnerStatus({
          ids: [item.id],
          status: "Approved",
          source: "Rejected",
        });
        const toastId = addToast(<ToastNotification icon={check} title="อนุมัติพาร์ทเนอร์แล้ว" description={`พาร์ทเนอร์ "${item.fname} ${item.lname}" ถูกอนุมัติแล้ว สามารถตรวจสอบได้ที่ "พาร์ทเนอร์ในระบบ"`} primaryButtonText="ดูพาร์ทเนอร์ในระบบ" onPrimaryClick={() => removeToast(toastId)} onSecondaryClick={() => removeToast(toastId)} variant="success" />, {
          autoDismiss: true,
        });
        // Refresh data
        forceRefresh();
      } catch (error) {
        console.error("Failed to approve partner:", error);
        const toastId = addToast(<ToastNotification icon={slash} title="เกิดข้อผิดพลาด" description="ไม่สามารถอนุมัติพาร์ทเนอร์ได้ กรุณาลองใหม่อีกครั้ง" onSecondaryClick={() => removeToast(toastId)} variant="danger" />, {
          autoDismiss: true,
        });
      }
    }
  };

  const handleApproveAllPartner = async () => {
    if (selectedRejectedPartners.length === 0) {
      const toastId = addToast(<ToastNotification icon={slash} title="ไม่มีรายการที่เลือก" description="กรุณาเลือกพาร์ทเนอร์ที่ต้องการอนุมัติอย่างน้อย 1 รายการ" onSecondaryClick={() => removeToast(toastId)} variant="warning" />, {
        autoDismiss: true,
      });
      return;
    }

    const result = await modal.alert.primary({
      title: "คุณยืนยันพาร์ทเนอร์ทั้งหมดนี้หรือไม่?",
      message: `คุณได้ทำการกดอนุมัติรายการพาร์ทเนอร์ที่มีอยู่ในระบบทั้งหมด "${selectedRejectedPartners.length} รายการ"`,
      confirmText: "ยืนยันที่จะอนุมัติ",
      cancelText: "ยกเลิก",
      children: (
        <div className="d-flex flex-column gap-3 divide-y">
          {selectedRejectedPartners.map((partner, index) => (
            <div key={partner.id} className={`d-flex flex-column gap-2 ${index > 0 && "pt-3"}`}>
              {renderUser(partner)}
            </div>
          ))}
        </div>
      ),
    });

    if (result) {
      try {
        await updatePartnerStatus({
          ids: selectedRejectedPartners.map((partner) => partner.id),
          status: "Approved",
          source: "Rejected",
        });
        const toastId = addToast(<ToastNotification variant="success" icon={check} title={`อนุมัติพาร์ทเนอร์แล้ว ${selectedRejectedPartners.length} รายการ`} description="สามารถไปตรวจสอบพาร์ทเนอร์ที่อนุมัติไปแล้วได้ที่ พาร์ทเนอร์ในระบบ" primaryButtonText="ดูพาร์ทเนอร์ในระบบ" onPrimaryClick={() => removeToast(toastId)} onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
        // Refresh data
        forceRefresh();
      } catch (error) {
        console.error("Failed to approve partners:", error);
        const toastId = addToast(<ToastNotification variant="danger" icon={slash} title="เกิดข้อผิดพลาด" description="ไม่สามารถอนุมัติพาร์ทเนอร์ได้ กรุณาลองใหม่อีกครั้ง" onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
      }
    }
  };

  return (
    <div className="table-container">
      <div className="text-lg mb-4">รายการพาร์ทเนอร์ Affiliate ที่ซ่อนไว้ทั้งหมด *{data?.pagination?.total?.toLocaleString() || 0} รายการ *</div>

      <CustomTable
        keyField="id"
        data={data?.data || []}
        columns={columns}
        selectable={true}
        onSelectionChange={(ids) => setSelectedRejectedPartners(ids)}
        selectionActions={
          <>
            <a className="success" onClick={handleApproveAllPartner}>
              <i className="bx bx-check-circle" />
              <span className="d-none d-md-block">อนุมัติ</span>
            </a>
          </>
        }
        itemsPerPage={data?.pagination?.limit || 10}
        totalItems={data?.pagination?.total || 0}
        onSort={handleSort}
        onFilter={handleFilter}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        status={isLoading ? "loading" : error ? "error" : data?.data?.length === 0 ? "empty" : "normal"}
        errorMessage={error?.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล"}
        emptyMessage="ไม่พบข้อมูลพาร์ทเนอร์ที่ซ่อนไว้"
      />
    </div>
  );
};

const withYupValidation = (WrappedComponent, schema) => {
  return function WithYupValidation(props) {
    // ตรวจสอบ validation เฉพาะเมื่อไม่มี error และไม่ได้กำลัง loading
    if (props.isLoading || props.error) {
      return <WrappedComponent {...props} />;
    }

    try {
      schema.validateSync(props.data);
      return <WrappedComponent {...props} />;
    } catch (error) {
      return (
        <div className="alert alert-danger">
          <strong>Validation Error:</strong> {error.message}
        </div>
      );
    }
  };
};

const ValidatedTable = withYupValidation(RejectedPartnersTable, RejectedPartnersSchema);

export default ValidatedTable;
